import { blogImageMaxBytes } from '../../api/blog';
import { maximumIndentLevel } from './indentExtension';

/**
 * Chuyển một file Word (.docx) thành nội dung bài blog.
 *
 * Giữ cấu trúc — tiêu đề mục, chữ đậm nghiêng gạch chân, danh sách, bảng, link, ảnh — cùng **căn lề và thụt
 * lề** của từng đoạn. Không giữ font, màu, cỡ chữ: bài hiển thị theo kiểu chữ chung của website.
 *
 * Thư viện đọc Word chỉ được tải về khi người dùng thật sự nhập một file, nên trang soạn bài không nặng thêm.
 */

/** Kết quả nhập một file Word. */
export interface WordImportResult {
  /** HTML để đổ vào trình soạn thảo. */
  readonly html: string;
  /** Số ảnh đã đưa lên kho. */
  readonly importedImages: number;
  /** Số ảnh bỏ qua vì trình duyệt không hiển thị được (EMF, WMF, TIFF...). */
  readonly unsupportedImages: number;
  /** Số ảnh bỏ qua vì quá 5 MB. */
  readonly oversizedImages: number;
  /** Số ảnh tải lên kho thất bại. */
  readonly failedImages: number;
}

/**
 * Một phần tử trong cây tài liệu mà thư viện đọc Word dựng ra — chỉ khai những trường dùng tới.
 *
 * Thư viện không công bố kiểu cho cây này, nên khai tối thiểu ở đây thay vì để kiểu "bất kỳ" lan ra.
 */
interface DocxElement {
  readonly type: string;
  readonly children?: readonly DocxElement[];
  readonly styleId?: string | null;
  readonly styleName?: string | null;
  readonly numbering?: unknown;
  readonly alignment?: string | null;
  readonly indent?: { readonly start?: string | null } | null;
}

type Alignment = 'left' | 'center' | 'right' | 'justify';

/** 0 là đoạn văn thường; 2–4 là cấp tiêu đề mục (cấp 1 dành cho tiêu đề bài trên website). */
type BlockLevel = 0 | 2 | 3 | 4;

const blockLevels: readonly BlockLevel[] = [0, 2, 3, 4];
const alignments: readonly Alignment[] = ['left', 'center', 'right', 'justify'];

/** Một bậc thụt lề trong Word là nửa inch = 720 twip — đúng một lần bấm Tab. */
const twipsPerIndentLevel = 720;

/** Kiểu ảnh trình duyệt hiển thị được, cùng đuôi tệp khớp quy tắc ảnh ở máy chủ. */
const imageExtensions: Readonly<Record<string, string>> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/gif': '.gif',
  'image/webp': '.webp',
};

/** Tiêu đề Word → cấp tiêu đề mục trong bài: Title và Heading 1 → lớn, Heading 2 → vừa, còn lại → nhỏ. */
function blockLevelOf(paragraph: DocxElement): BlockLevel {
  const style = `${paragraph.styleId ?? ''} ${paragraph.styleName ?? ''}`.toLowerCase();

  if (/\btitle\b/.test(style)) {
    return 2;
  }

  const match = /heading\s*(\d)/.exec(style);

  if (match === null) {
    return 0;
  }

  const level = Number(match[1]);

  return level <= 1 ? 2 : level === 2 ? 3 : 4;
}

function alignmentOf(paragraph: DocxElement): Alignment {
  switch (paragraph.alignment) {
    case 'center':
      return 'center';
    case 'right':
    case 'end':
      return 'right';
    case 'both':
    case 'distribute':
      return 'justify';
    default:
      return 'left';
  }
}

function indentLevelOf(paragraph: DocxElement): number {
  const twips = Number.parseInt(paragraph.indent?.start ?? '', 10);

  if (!Number.isFinite(twips) || twips <= 0) {
    return 0;
  }

  return Math.min(maximumIndentLevel, Math.round(twips / twipsPerIndentLevel));
}

/** Tên kiểu đánh dấu cho một tổ hợp cấp, căn lề, thụt lề — để bảng ánh xạ kiểu biết sinh thẻ gì. */
function marker(level: BlockLevel, alignment: Alignment, indent: number): string {
  return `erp-${level.toString()}-${alignment}-${indent.toString()}`;
}

/**
 * Gắn kiểu đánh dấu cho từng đoạn văn, giữ nguyên các nút còn lại.
 *
 * Thư viện chỉ cho đổi thẻ HTML theo tên kiểu đoạn, không cho thêm thuộc tính kiểu dáng. Vì vậy căn lề và thụt
 * lề được mã hóa vào tên kiểu, đổi thành lớp CSS ở bảng ánh xạ, rồi đổi tiếp thành `style` ở bước hậu xử lý.
 * Đoạn thuộc danh sách giữ nguyên để thư viện vẫn nhận ra danh sách.
 */
function markParagraphs(element: DocxElement): DocxElement {
  const children = element.children?.map(markParagraphs);
  const withChildren: DocxElement = children === undefined ? element : { ...element, children };

  if (element.type !== 'paragraph' || (element.numbering !== null && element.numbering !== undefined)) {
    return withChildren;
  }

  return {
    ...withChildren,
    styleName: marker(blockLevelOf(element), alignmentOf(element), indentLevelOf(element)),
  };
}

/** Bảng ánh xạ kiểu: mọi tổ hợp cấp × căn lề × thụt lề, cộng chữ gạch chân và gạch ngang. */
function buildStyleMap(): string[] {
  const rules = ['u => u', 'strike => s'];

  for (const level of blockLevels) {
    for (const alignment of alignments) {
      for (let indent = 0; indent <= maximumIndentLevel; indent++) {
        const tag = level === 0 ? 'p' : `h${level.toString()}`;
        const alignClass = alignment === 'left' ? '' : `.erp-align-${alignment}`;
        const indentClass = indent === 0 ? '' : `.erp-indent-${indent.toString()}`;

        rules.push(`p[style-name='${marker(level, alignment, indent)}'] => ${tag}${alignClass}${indentClass}:fresh`);
      }
    }
  }

  return rules;
}

/** Đổi lớp đánh dấu thành `style` mà trình soạn thảo hiểu; bỏ ảnh không tải lên được. */
function finalizeHtml(html: string): string {
  const document = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');

  for (const element of document.body.querySelectorAll<HTMLElement>('[class]')) {
    for (const className of element.classList) {
      const alignment = /^erp-align-(center|right|justify)$/.exec(className);

      if (alignment !== null) {
        element.style.textAlign = alignment[1] ?? 'left';
      }

      const indent = /^erp-indent-(\d+)$/.exec(className);

      if (indent !== null) {
        element.style.marginLeft = `${(Number(indent[1]) * 2).toString()}em`;
      }
    }

    element.removeAttribute('class');
  }

  for (const image of document.body.querySelectorAll('img')) {
    if ((image.getAttribute('src') ?? '') === '') {
      image.remove();
    }
  }

  // Ảnh trong trình soạn thảo là một khối riêng, không nằm trong đoạn văn. Đoạn chỉ chứa ảnh mà để nguyên
  // thì trình soạn thảo tách nó ra và để lại một dòng trống phía trước — bỏ lớp đoạn bọc ngoài đi.
  for (const paragraph of document.body.querySelectorAll('p')) {
    const images = paragraph.querySelectorAll('img');
    const onlyImages =
      images.length > 0
      && [...paragraph.childNodes].every(
        (node) => node instanceof HTMLImageElement || (node.textContent ?? '').trim() === '',
      );

    if (onlyImages) {
      paragraph.replaceWith(...images);
    }
  }

  return document.body.innerHTML;
}

/**
 * Đọc một file .docx và trả về nội dung bài.
 *
 * @param file File Word người dùng chọn.
 * @param uploadImage Hàm đưa một ảnh lên kho, trả về địa chỉ công khai.
 */
export async function importWordDocument(
  file: File,
  uploadImage: (image: File) => Promise<string>,
): Promise<WordImportResult> {
  const mammoth = (await import('mammoth')).default;

  let importedImages = 0;
  let unsupportedImages = 0;
  let oversizedImages = 0;
  let failedImages = 0;

  const result = await mammoth.convertToHtml(
    { arrayBuffer: await file.arrayBuffer() },
    {
      styleMap: buildStyleMap(),
      transformDocument: (element: unknown) => markParagraphs(element as DocxElement),
      convertImage: mammoth.images.imgElement(async (image) => {
        const extension = imageExtensions[image.contentType.toLowerCase()];

        if (extension === undefined) {
          unsupportedImages++;

          return { src: '' };
        }

        const content = await image.readAsArrayBuffer();

        if (content.byteLength > blogImageMaxBytes) {
          oversizedImages++;

          return { src: '' };
        }

        try {
          const url = await uploadImage(new File([content], `anh-tu-word${extension}`, { type: image.contentType }));

          importedImages++;

          return { src: url };
        } catch {
          failedImages++;

          return { src: '' };
        }
      }),
    },
  );

  return {
    html: finalizeHtml(result.value),
    importedImages,
    unsupportedImages,
    oversizedImages,
    failedImages,
  };
}
